import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { cameraRight, eyeHeight, lookPoint, looksRng, Precip, SkyLook, TAU, type OmenCtx, type OmenScene } from './common.ts';

/**
 * The blood moon: crimson moonlight over a black-red sky, motes of red drifting up from the ground, the
 * nightmares' eyes burning red, and the blood the heroes drink: when a hero's blow heals them, a red wisp
 * flies from the nightmare they struck into them. Fallen nightmares rise as red mist.
 */

const RED = new THREE.Color('#ff3b2e');
const DEEP = new THREE.Color('#a0101a');
const PALE = new THREE.Color('#ff8a6e');

interface Wisp {
  a: THREE.Vector3;
  heroId: number;
  t0: number;
  dur: number;
  bend: THREE.Vector3;
}

export const buildBloodMoon = (ctx: OmenCtx): OmenScene => {
  const group = new THREE.Group();
  group.name = 'omen-bloodmoon';
  const low = ctx.low;
  const rnd = looksRng(0xb100d);
  const sky = new SkyLook(ctx, {
    hemi: ['#9a8a96', '#1c0a10', 0.8],
    key: ['#ffb0a2', 0.8],
    rim: ['#ff2a40', 1.7],
    background: '#1c0608',
    fog: '#22080b',
    mist: { low: '#5a1a1e', mid: '#3a1014', far: '#1c0608', alpha: 1.2 },
  });
  const motes = new Precip({ count: low ? 110 : 320, box: [13, 4, 13], fall: -0.35, sway: 0.3, size: [0.05, 0.05], color: '#ff4030', opacity: 0.75, round: true, additive: true, twinkle: 2.5 }, 91);
  const glow = new GlowLayer(1024);
  group.add(motes.mesh, glow.mesh);
  const wisps: Wisp[] = [];
  const lastWisp = new Map<number, number>();
  let enemies: { id: number; kind: string }[] = [];
  let now = 0;
  let tickN = 0;
  const look = new THREE.Vector3();
  const right = new THREE.Vector3();
  const tmp = new THREE.Vector3();

  return {
    group,
    tick: (s: BattleState) => {
      tickN++;
      enemies = s.enemies.filter((e) => e.hp > 0 && e.kind !== 'boneWall').map((e) => ({ id: e.id, kind: e.kind }));
    },
    event: (s, e) => {
      if (e.t === 'heal') {
        // A hero drinking from a blow: a wisp from the nearest nightmare (one every few ticks per hero).
        const h = s.heroes.find((x) => x.id === e.id);
        if (!h || (lastWisp.get(h.id) ?? -9) > tickN - 3) return false;
        let best: (typeof s.enemies)[number] | null = null;
        let bd = 3000 * 3000;
        for (const n of s.enemies) {
          if (n.hp <= 0) continue;
          const d = (n.x - h.x) ** 2 + (n.y - h.y) ** 2;
          if (d < bd) {
            bd = d;
            best = n;
          }
        }
        if (!best) return false;
        lastWisp.set(h.id, tickN);
        const a = ctx.pos(best.id)?.clone() ?? ctx.at(best.x, best.y);
        a.y += eyeHeight(best.kind) * 0.8;
        const ang = rnd() * TAU;
        wisps.push({ a, heroId: h.id, t0: now, dur: 0.45 + rnd() * 0.2, bend: new THREE.Vector3(Math.cos(ang) * 0.6, 0.7 + rnd() * 0.5, Math.sin(ang) * 0.6) });
        if (wisps.length > 40) wisps.shift();
        return false;
      }
      if (e.t === 'die') {
        const p = ctx.at(e.x, e.y, 0.3);
        ctx.vfx.burst(p, { n: low ? 3 : 7, color: '#6a0c14', speed: 0.03, up: 0.05, size: 0.45, life: 18, smoke: true, spread: 0.3, gravity: -0.004 });
        ctx.vfx.burst(p, { n: low ? 4 : 9, color: '#ff3b2e', speed: 0.05, up: 0.09, size: 0.07, life: 14, gravity: -0.003 });
      }
      return false;
    },
    frame: (t, camera) => {
      now = t;
      lookPoint(camera, look);
      motes.frame(t, look);
      // The moonlight swells and ebbs slowly, as if clouds crossed it.
      const k = 0.92 + 0.08 * Math.sin(t * 0.6) + 0.04 * Math.sin(t * 1.7);
      sky.pulse({ key: k, rim: 0.9 + 0.2 * Math.sin(t * 0.8) });
      cameraRight(camera, right);
      glow.begin();
      // Eyes burning red.
      for (const e of enemies) {
        const p = ctx.pos(e.id);
        if (!p) continue;
        const y = p.y + eyeHeight(e.kind);
        const blink = Math.sin(t * 0.7 + e.id * 1.3) > 0.97 ? 0.1 : 1;
        for (const sgn of [-1, 1]) glow.add(p.x + right.x * 0.05 * sgn, y, p.z + right.z * 0.05 * sgn, RED, 0.09, 0.95 * blink);
        glow.add(p.x, y, p.z, DEEP, 0.4, 0.25 * blink);
      }
      // Wisps of blood flying from the struck into the striker, with a short tail.
      for (let i = wisps.length - 1; i >= 0; i--) {
        const w = wisps[i] as Wisp;
        const k2 = (t - w.t0) / w.dur;
        const hp = ctx.pos(w.heroId);
        if (k2 >= 1 || !hp) {
          if (hp && k2 >= 1 && k2 < 1.2) glow.add(hp.x, hp.y + 0.5, hp.z, PALE, 0.9, 0.5 * (1 - (k2 - 1) / 0.2));
          if (k2 >= 1.2 || !hp) wisps.splice(i, 1);
          continue;
        }
        const b = tmp.copy(hp).setY(hp.y + 0.5);
        for (let j = 0; j < 6; j++) {
          const kk = Math.max(0, k2 - j * 0.05);
          const ease = kk * kk * (3 - 2 * kk);
          const x = w.a.x + (b.x - w.a.x) * ease + w.bend.x * Math.sin(kk * Math.PI);
          const y = w.a.y + (b.y - w.a.y) * ease + w.bend.y * Math.sin(kk * Math.PI);
          const z = w.a.z + (b.z - w.a.z) * ease + w.bend.z * Math.sin(kk * Math.PI);
          const f = 1 - j / 6;
          glow.add(x, y, z, j === 0 ? PALE : RED, 0.22 * f + 0.05, 0.9 * f);
        }
      }
      glow.end();
    },
    dispose: () => {
      sky.restore();
      motes.dispose();
      glow.mesh.geometry.dispose();
      glow.material.dispose();
    },
  };
};
